import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { AppBar } from "./mobileUi";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";
import { FiChevronLeft } from "react-icons/fi";
import ReactPaginate from "react-paginate";

/**
 * ===================================================================
 * Bộ UI cho bản WEB (desktop) của employeeApp.
 *
 * Mục tiêu: màn hình từ breakpoint md trở lên dùng đúng style trang
 * quản trị (admin) của FE — tiêu đề trang, nút gradient, thẻ thống kê,
 * bảng có hàng filter + phân trang — thay cho khung điện thoại 480px.
 * Bản mobile (< md) giữ nguyên UI giống app-v2, KHÔNG dùng file này.
 *
 * Chỉ là lớp trình bày: không chứa logic nghiệp vụ, không gọi API.
 * ===================================================================
 */

/** true khi màn hình >= 768px (breakpoint md của theme). */
export const useIsDesktop = (query = "(min-width: 768px)") => {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

/** Token màu dùng lại của admin (global.css). */
export const DESK = {
  pageBg: "var(--color-backgroundmain)",
  surface: "var(--color-main)",
  soft: "var(--fill-avatar)",
  border: "var(--border-input-employee)",
  title: "var(--color-header-employee)",
  label: "var(--text-color-Subtittle)",
  info: "var(--color-info-employee)",
  primary: "var(--color-secondary)",
  buttonBg: "var(--color-button)",
  excelBg: "var(--linear-gradient-excel)",
  redBg: "var(--linear-gradient-red)",
};

/* ===================== Khung trang ===================== */

export const DesktopPage = ({ title, subtitle, right, children, ...props }) => (
  <Flex direction="column" gap="16px" w="100%" {...props}>
    {(title || right) && (
      <Flex align="center" justify="space-between" gap="16px" flexWrap="wrap">
        <Box>
          {title && (
            <Text fontSize="24px" fontWeight={600} color={DESK.title}>
              {title}
            </Text>
          )}
          {subtitle && (
            <Text fontSize="14px" color={DESK.label} mt="4px">
              {subtitle}
            </Text>
          )}
        </Box>
        {right}
      </Flex>
    )}
    {children}
  </Flex>
);

/** Link quay lại kiểu web (thay cho nút back của AppBar mobile). */
export const DesktopBack = ({ label = "Quay lại", onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap="6px"
    color={DESK.label}
    _hover={{ color: DESK.primary }}
    fontSize="14px"
    fontWeight={500}
    w="fit-content"
  >
    <Box as={FiChevronLeft} fontSize="18px" />
    {label}
  </Flex>
);

/** Panel trắng bo góc 12 — hộp nội dung chuẩn của admin. */
export const DesktopPanel = ({ children, ...props }) => (
  <Box bg={DESK.surface} borderRadius="12px" p="16px" {...props}>
    {children}
  </Box>
);


/**
 * Khung trang tự đổi theo bề ngang màn hình:
 * - desktop: tiêu đề + nội dung theo style admin
 * - mobile : giữ nguyên cột 480px + AppBar như app-v2
 * Dùng cho các màn chỉ khác nhau ở phần khung (menu chức năng, form...).
 */
export const ResponsivePage = ({
  title,
  onBack,
  right,
  children,
  mobileProps,
  ...props
}) => {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DesktopPage
        title={title}
        right={right ?? (onBack ? <DesktopBack onClick={onBack} /> : null)}
        {...props}
      >
        {children}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px" {...mobileProps}>
      <AppBar title={title} onBack={onBack} />
      {children}
    </Box>
  );
};

/* ===================== Tabs ===================== */

export const DesktopTabs = ({ items = [], value, onChange }) => (
  <Flex align="center" gap="32px" flexWrap="wrap">
    {items.map((item) => {
      const active = item.value === value;
      return (
        <Flex
          key={item.value}
          as="button"
          type="button"
          onClick={() => onChange && onChange(item.value)}
          direction="column"
          align="center"
          gap="8px"
        >
          <Text
            fontSize="16px"
            fontWeight={600}
            whiteSpace="nowrap"
            className={active ? "text-liner" : undefined}
            color={active ? undefined : "var(--color-text-unclick)"}
          >
            {item.label}
          </Text>
          <Box
            h="2px"
            w="100%"
            borderRadius="2px"
            bgImage={active ? DESK.buttonBg : "none"}
          />
        </Flex>
      );
    })}
  </Flex>
);

/* ===================== Nút ===================== */

const BUTTON_BG = {
  primary: DESK.buttonBg,
  excel: DESK.excelBg,
  danger: DESK.redBg,
};

export const DesktopButton = ({
  variant = "primary",
  icon,
  children,
  ...props
}) => {
  if (variant === "outline") {
    return (
      <Button
        h="44px"
        p="10px 16px"
        bg={DESK.surface}
        border="1px solid"
        borderColor={DESK.primary}
        color={DESK.primary}
        fontSize="15px"
        fontWeight={600}
        _hover={{ bg: DESK.soft }}
        {...props}
      >
        <Flex align="center" gap="8px">
          {icon}
          {children}
        </Flex>
      </Button>
    );
  }

  return (
    <Button
      h="44px"
      p="10px 16px"
      backgroundImage={BUTTON_BG[variant] || DESK.buttonBg}
      color="var(--color-main)"
      fontSize="15px"
      fontWeight={600}
      _hover={{ filter: "brightness(96%)" }}
      _active={{ filter: "brightness(90%)" }}
      _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
      {...props}
    >
      <Flex align="center" gap="8px">
        {icon}
        {children}
      </Flex>
    </Button>
  );
};

/* ===================== Thẻ thống kê ===================== */

export const DesktopStatRow = ({ items = [], minW = "192px" }) => {
  if (!items.length) return null;
  return (
    <Flex align="center" gap="16px" flexWrap="wrap">
      {items.map((item, index) => (
        <Flex
          key={item.key || item.title || index}
          minW={minW}
          direction="column"
          gap="12px"
          bg={DESK.surface}
          p="16px 42px 16px 16px"
          borderRadius="12px"
        >
          <Text fontSize="15px" fontWeight={400} color={DESK.label}>
            {item.title}
          </Text>
          <Text
            fontSize="20px"
            fontWeight={500}
            color={item.color || "#000"}
            whiteSpace="nowrap"
          >
            {item.total}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

/* ===================== Ô lọc ===================== */

export const DesktopSearch = ({
  placeholder = "Tìm kiếm...",
  value,
  defaultValue,
  onChange,
  maxW = "42vw",
  ...props
}) => (
  <InputGroup maxW={maxW} w="100%" borderRadius="8px" {...props}>
    <Input
      placeholder={placeholder}
      type="text"
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      borderRadius="8px"
      border="1px solid var(--color-secondary)"
      backgroundColor={DESK.soft}
      fontSize="14px"
    />
    <InputRightElement
      borderTopRightRadius="8px"
      borderBottomRightRadius="8px"
      backgroundImage={DESK.buttonBg}
      pointerEvents="none"
    >
      <SearchIcon color="var(--color-main)" />
    </InputRightElement>
  </InputGroup>
);

/** Select lọc — dùng cho hàng filter dưới tiêu đề bảng và thanh lọc trên. */
export const DesktopSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Tất cả",
  w = "100%",
  minW = "140px",
  ...props
}) => (
  <Select
    value={value ?? ""}
    onChange={(e) => onChange && onChange(e.target.value)}
    w={w}
    minW={minW}
    h="36px"
    fontSize="13px"
    bg={DESK.surface}
    borderRadius="8px"
    borderColor={DESK.border}
    _focusVisible={{ borderColor: DESK.primary, boxShadow: "none" }}
    {...props}
  >
    <option value="">{placeholder}</option>
    {options.map((opt) => (
      <option key={String(opt.value)} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </Select>
);

export const DesktopDateInput = ({ value, onChange, w = "100%", ...props }) => (
  <Input
    type="date"
    value={value || ""}
    onChange={(e) => onChange && onChange(e.target.value)}
    w={w}
    h="36px"
    fontSize="13px"
    bg={DESK.surface}
    borderRadius="8px"
    borderColor={DESK.border}
    _focusVisible={{ borderColor: DESK.primary, boxShadow: "none" }}
    {...props}
  />
);

/** Thanh lọc nằm trên bảng (ô tìm kiếm + các select). */
export const DesktopFilterBar = ({ children, ...props }) => (
  <Flex
    direction="column"
    align="flex-start"
    gap="16px"
    p="16px 16px 12px 16px"
    {...props}
  >
    {children}
  </Flex>
);

/* ===================== Bảng ===================== */

/**
 * headers: [{ key, label, width, align, sortable, render(row, index) }]
 * rows:    mảng dữ liệu đã format sẵn ở trang cha (không đụng logic)
 * filters: { [key]: ReactNode } — control lọc đặt ở hàng ngay dưới tiêu đề
 * sortStates / onSort: tuỳ chọn, chỉ để hiển thị mũi tên sắp xếp
 */
export const DesktopTable = ({
  headers = [],
  rows = [],
  filters,
  sortStates = {},
  onSort,
  loading = false,
  emptyText = "Không có dữ liệu",
  onRowClick,
  maxH,
  ...props
}) => {
  const hasFilters = useMemo(
    () => !!filters && Object.keys(filters).length > 0,
    [filters],
  );

  return (
    <Skeleton isLoaded={!loading} borderRadius="12px">
      <TableContainer w="100%" pb="16px" maxH={maxH} overflowY="auto" {...props}>
        <Table w="100%" variant="unstyled">
          <Thead
            position="sticky"
            top="0"
            zIndex="1"
            w="100%"
            backgroundColor={DESK.soft}
          >
            <Tr h="41px">
              {headers.map((header) => {
                const state = sortStates[header.key];
                const sortable = header.sortable && onSort;
                return (
                  <Td
                    key={header.key}
                    p="16px 16px 8px 16px"
                    border="none"
                    color={DESK.label}
                    fontSize="14px"
                    w={header.width}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      gap="4px"
                      fontWeight="bold"
                      cursor={sortable ? "pointer" : "default"}
                      onClick={() => sortable && onSort(header.key)}
                    >
                      <Text
                        whiteSpace="nowrap"
                        color={
                          state === 1 || state === -1
                            ? "var(--color-option-employee-hover)"
                            : undefined
                        }
                      >
                        {header.label}
                      </Text>
                      {state === 1 ? (
                        <TiArrowSortedUp color="var(--color-option-employee-hover)" />
                      ) : state === -1 ? (
                        <TiArrowSortedDown color="var(--color-option-employee-hover)" />
                      ) : null}
                    </Flex>
                  </Td>
                );
              })}
            </Tr>
            {hasFilters && (
              <Tr backgroundColor={DESK.soft}>
                {headers.map((header) => (
                  <Td
                    key={header.key}
                    p="0px 16px 12px 16px"
                    border="none"
                    verticalAlign="top"
                  >
                    {filters[header.key] || null}
                  </Td>
                ))}
              </Tr>
            )}
          </Thead>
          <Tbody w="100%">
            {rows.length ? (
              rows.map((row, rowIndex) => (
                <Tr
                  key={row.id ?? rowIndex}
                  h="72px"
                  backgroundColor={
                    rowIndex % 2 === 0 ? "transparent" : DESK.soft
                  }
                  cursor={onRowClick ? "pointer" : "default"}
                  _hover={onRowClick ? { backgroundColor: "#EFF6FF" } : undefined}
                  onClick={() => onRowClick && onRowClick(row, rowIndex)}
                >
                  {headers.map((header) => (
                    <Td
                      key={header.key}
                      p="16px"
                      w={header.width}
                      fontSize="14px"
                      color={DESK.info}
                      textAlign={header.align || "center"}
                      whiteSpace="normal"
                    >
                      {header.render
                        ? header.render(row, rowIndex)
                        : row[header.key] ?? "-"}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td
                  colSpan={headers.length}
                  textAlign="center"
                  p="40px 16px"
                  color={DESK.label}
                  fontSize="14px"
                >
                  {emptyText}
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Skeleton>
  );
};

/** Phân trang kiểu admin: "Showing x to y of z entries" + ReactPaginate. */
export const DesktopPagination = ({
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
}) => {
  const totalPages = Math.max(Math.ceil((total || 0) / (pageSize || 1)), 1);
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, total || 0);

  return (
    <Flex m="24px 16px 16px 16px" align="center" justify="space-between" gap="16px">
      <Text fontSize="14px" fontWeight={500} lineHeight="20px" color={DESK.label}>
        Showing {start} to {end} of {total || 0} entries
      </Text>
      <HStack spacing={2} justify="flex-end">
        <ReactPaginate
          className="pagination-custom"
          pageCount={totalPages}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          onPageChange={(e) => onPageChange && onPageChange(e.selected + 1)}
          containerClassName="pagination"
          activeClassName="active"
          breakClassName="ellipsis"
          breakLabel="..."
          previousLabel={<Box as={FiChevronLeft} fontSize="16px" />}
          nextLabel={
            <Box as={FiChevronLeft} fontSize="16px" transform="rotate(180deg)" />
          }
          renderOnZeroPageCount={null}
          forcePage={Math.min(page, totalPages) - 1}
        />
      </HStack>
    </Flex>
  );
};

/**
 * Bảng + thanh lọc + phân trang gói trong một panel trắng — bố cục
 * chuẩn của các màn danh sách bên admin.
 */
export const DesktopListPanel = ({
  filterBar,
  headers,
  rows,
  filters,
  sortStates,
  onSort,
  loading,
  emptyText,
  onRowClick,
  page,
  pageSize,
  total,
  onPageChange,
  showPagination = true,
}) => (
  <Flex
    direction="column"
    bg={DESK.surface}
    borderRadius="12px"
    minH="calc(100vh - 260px)"
    justify="space-between"
  >
    <Box>
      {filterBar ? <DesktopFilterBar>{filterBar}</DesktopFilterBar> : null}
      <DesktopTable
        headers={headers}
        rows={rows}
        filters={filters}
        sortStates={sortStates}
        onSort={onSort}
        loading={loading}
        emptyText={emptyText}
        onRowClick={onRowClick}
      />
    </Box>
    {showPagination && (
      <DesktopPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
      />
    )}
  </Flex>
);


/**
 * Thanh lọc dùng chung cho các màn danh sách đơn (nghỉ phép / chấm công ngoài /
 * làm bù / phê duyệt): trạng thái + khoảng ngày + sắp xếp.
 * Nhận thẳng state/setter sẵn có của trang nên không đổi logic lọc.
 */
export const DesktopRequestFilters = ({
  status,
  onStatusChange,
  statusOptions = [
    { value: "PENDING", label: "Chờ duyệt" },
    { value: "APPROVED", label: "Đã duyệt" },
    { value: "REJECTED", label: "Từ chối" },
  ],
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  sortOrder,
  onSortChange,
  fromLabel = "Từ ngày",
  toLabel = "Đến ngày",
  children,
}) => (
  <Flex align="flex-end" gap="16px" p="16px" flexWrap="wrap">
    {onStatusChange && (
      <Box w="200px">
        <Text fontSize="13px" color={DESK.label} mb="4px">
          Trạng thái
        </Text>
        <DesktopSelect
          options={statusOptions}
          value={status === "ALL" ? "" : status}
          placeholder="Tất cả"
          onChange={(v) => onStatusChange(v || "ALL")}
        />
      </Box>
    )}
    {onFromChange && (
      <Box w="180px">
        <Text fontSize="13px" color={DESK.label} mb="4px">
          {fromLabel}
        </Text>
        <DesktopDateInput value={fromDate} onChange={onFromChange} />
      </Box>
    )}
    {onToChange && (
      <Box w="180px">
        <Text fontSize="13px" color={DESK.label} mb="4px">
          {toLabel}
        </Text>
        <DesktopDateInput value={toDate} onChange={onToChange} />
      </Box>
    )}
    {onSortChange && (
      <Box w="180px">
        <Text fontSize="13px" color={DESK.label} mb="4px">
          Sắp xếp
        </Text>
        <DesktopSelect
          options={[
            { value: "desc", label: "Mới đến cũ" },
            { value: "asc", label: "Cũ đến mới" },
          ]}
          value={sortOrder}
          placeholder="Mới đến cũ"
          onChange={(v) => onSortChange(v || "desc")}
        />
      </Box>
    )}
    {children}
  </Flex>
);

/* ===================== Trang chi tiết ===================== */

export const DetailSection = ({ title, right, children, columns = 3, ...props }) => (
  <DesktopPanel {...props}>
    {(title || right) && (
      <Flex align="center" justify="space-between" mb="16px" gap="12px">
        {title && (
          <Text fontSize="16px" fontWeight={600} color={DESK.title}>
            {title}
          </Text>
        )}
        {right}
      </Flex>
    )}
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      columnGap="24px"
      rowGap="16px"
    >
      {children}
    </Box>
  </DesktopPanel>
);

export const DetailItem = ({ label, value, children, span = 1 }) => (
  <Box gridColumn={span > 1 ? `span ${span}` : undefined}>
    <Text fontSize="13px" color={DESK.label} mb="4px">
      {label}
    </Text>
    <Box fontSize="14px" fontWeight={600} color={DESK.info} wordBreak="break-word">
      {children ?? (value === 0 || value ? value : "-")}
    </Box>
  </Box>
);
